import { useTranslation } from 'react-i18next';
import { Card } from '../common';
import { useCalculationStore } from '../../stores/calculationStore';
import { useProductionStore } from '../../stores/productionStore';
import { usePricingStore } from '../../stores/pricingStore';
import { useUnitStore, KG_TO_LB } from '../../stores/unitStore';

export function PerformanceMetrics() {
  const { t } = useTranslation();
  const { weightUnit } = useUnitStore();
  const metrics = useCalculationStore((state) => state.metrics);
  const revenue = useCalculationStore((state) => state.revenue);

  if (!metrics) {
    return (
      <Card title={t('results.performanceMetrics')}>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          {t('results.enterRequiredData')}
        </p>
      </Card>
    );
  }

  // Calculate derived values from production data
  const productionStore = useProductionStore.getState();
  const pricingParams = usePricingStore.getState().parameters;
  const harvestEvents = productionStore.harvestEvents;
  const totalCount = harvestEvents.reduce((sum, event) => sum + event.count, 0);
  const totalWeight = harvestEvents.reduce((sum, event) => sum + (event.totalWeight || 0), 0);
  const averageWeight = totalCount > 0 ? totalWeight / totalCount : 0;

  // Calculate feed bonus/penalty details
  const fcrDifference = metrics.targetFCR - metrics.fcr;
  const savedKg = fcrDifference * totalWeight;
  
  // Calculate max allowed feed consumption
  const maxAllowedFeed = metrics.targetFCR * totalWeight;
  
  // Calculate chick bonus/penalty details
  const productionData = productionStore.data;
  const allowedDeaths = productionData.chickCount * (pricingParams.chickMortalityLimit / 100);
  const actualDeaths = productionData.chickCount - totalCount;
  const chickDifference = allowedDeaths - actualDeaths;

  const getFCRColor = (actual: number, target: number): string => {
    if (actual <= target) {
      return 'text-green-600 dark:text-green-400';
    }
    return 'text-red-600 dark:text-red-400';
  };

  const getEPEFColor = (epef: number): string => {
    if (epef >= 450) return 'text-green-600 dark:text-green-400';
    if (epef >= 400) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <Card title={t('results.performanceMetrics')}>
      <div className="space-y-3">
        {/* Main Metrics Grid - 2x2 */}
        <div className="grid grid-cols-2 gap-3">
          {/* Cycle Days */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
            <div className="text-xs text-blue-700 dark:text-blue-300 mb-1 font-medium">
              {t('results.cycleDays')}
            </div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {metrics.cycleDays}
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
              {t('results.days')}
            </div>
          </div>

          {/* Survival Rate */}
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800">
            <div className="text-xs text-green-700 dark:text-green-300 mb-1 font-medium">
              {t('results.survivalRate')}
            </div>
            <div className="text-2xl font-bold text-green-900 dark:text-green-100">
              {metrics.survivalRate.toFixed(2)}%
            </div>
          </div>

          {/* Total Count */}
          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
            <div className="text-xs text-purple-700 dark:text-purple-300 mb-1 font-medium">
              {t('results.totalCount')}
            </div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {totalCount.toLocaleString('tr-TR')}
            </div>
          </div>

          {/* Average Weight */}
          <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-100 dark:border-orange-800">
            <div className="text-xs text-orange-700 dark:text-orange-300 mb-1 font-medium">
              {t('results.averageWeight')}
            </div>
            <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
              {weightUnit === 'lb' ? (averageWeight * KG_TO_LB).toFixed(3) : averageWeight.toFixed(3)}
            </div>
            <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
              {weightUnit}
            </div>
          </div>
        </div>

        {/* Feed Bonus/Penalty Details */}
        {revenue && (
          <div className="grid grid-cols-2 gap-3">
            {/* Feed Bonus/Penalty */}
            <div className={`p-3 rounded-lg border ${savedKg >= 0 ? 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800'}`}>
              <div className={`text-xs mb-1 font-medium ${savedKg >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {savedKg >= 0 ? t('results.feedSavings') : t('results.feedLoss')}
              </div>
              <div className={`text-2xl font-bold ${savedKg >= 0 ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                {weightUnit === 'lb' ? (Math.abs(savedKg) * KG_TO_LB).toFixed(2) : Math.abs(savedKg).toFixed(2)}
              </div>
              <div className={`text-xs mt-0.5 ${savedKg >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {weightUnit}
              </div>
            </div>

            {/* Chick Bonus/Penalty */}
            <div className={`p-3 rounded-lg border ${chickDifference >= 0 ? 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800'}`}>
              <div className={`text-xs mb-1 font-medium ${chickDifference >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {chickDifference >= 0 ? t('results.chickBonusLabel') : t('results.chickPenaltyLabel')}
              </div>
              <div className={`text-2xl font-bold ${chickDifference >= 0 ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                {Math.abs(chickDifference).toFixed(0)}
              </div>
              <div className={`text-xs mt-0.5 ${chickDifference >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {t('results.count')}
              </div>
            </div>
          </div>
        )}

        {/* FCR - Full Width */}
        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg border border-blue-200 dark:border-blue-700">
          <div className="flex justify-between items-start mb-2">
            <div className="text-sm text-blue-700 dark:text-blue-300 font-medium">
              {t('results.fcr')}
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-800/50 px-2 py-0.5 rounded">
              {metrics.fcrSource === 'manual' ? t('results.manual') : t('results.table')}
            </div>
          </div>
          <div className="flex items-baseline gap-3">
            <div className={`text-4xl font-bold ${getFCRColor(metrics.fcr, metrics.targetFCR)}`}>
              {metrics.fcr.toFixed(3)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              / {t('results.targetFCR')}: {metrics.targetFCR.toFixed(3)}
            </div>
          </div>
          {metrics.fcr <= metrics.targetFCR && (
            <div className="text-xs text-green-600 dark:text-green-400 mt-2 font-medium">
              ✓ {t('results.belowTarget')}
            </div>
          )}
          {metrics.fcr > metrics.targetFCR && (
            <div className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">
              ✗ {t('results.aboveTarget')}
            </div>
          )}
        </div>

        {/* Max Allowed Feed - Full Width */}
        <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 rounded-lg border border-amber-200 dark:border-amber-700">
          <div className="flex justify-between items-start mb-2">
            <div className="text-sm text-amber-700 dark:text-amber-300 font-medium">
              {t('results.maxFeedConsumption')}
            </div>
            <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-800/50 px-2 py-0.5 rounded">
              {metrics.fcrSource === 'manual' ? t('results.manual') : t('results.table')}
            </div>
          </div>
          <div className="flex items-baseline gap-3">
            <div className="text-4xl font-bold text-amber-900 dark:text-amber-100">
              {(weightUnit === 'lb' ? maxAllowedFeed * KG_TO_LB : maxAllowedFeed).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {weightUnit}
            </div>
          </div>
          <div className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            {t('results.targetFCRxWeight')}
          </div>
        </div>

        {/* EPEF - Full Width */}
        <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-lg border border-purple-200 dark:border-purple-700">
          <div className="text-sm text-purple-700 dark:text-purple-300 font-medium mb-2">
            {t('results.epef')}
          </div>
          <div className="flex items-baseline gap-3">
            <div className={`text-4xl font-bold ${getEPEFColor(metrics.epef)}`}>
              {metrics.epef.toFixed(2)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {metrics.epef >= 450 && `⭐ ${t('results.excellent')}`}
              {metrics.epef >= 400 && metrics.epef < 450 && `✓ ${t('results.good')}`}
              {metrics.epef < 400 && `⚠ ${t('results.needsImprovement')}`}
            </div>
          </div>
        </div>

        {/* Total Weight */}
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              {t('results.totalWeight')}
            </span>
            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {(weightUnit === 'lb' ? totalWeight * KG_TO_LB : totalWeight).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {weightUnit}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
